import { AnimatePresence, motion } from "motion/react";

import { useTheme } from "@up/data";
import { MOTION, Toast, ToastTitle } from "@up/ui";

import { AddonLineItem } from "../AddonLineItem";
import { AvailableCodes } from "../AvailableCodes";
import { BlockingErrorsMessage } from "../BlockingErrorsMessage";
import { useCart } from "../CartContext";
import { DiscountSection } from "../DiscountSection";
import FinalAgreement from "../FinalAgreement";
import { GuardianSection } from "../GuardianSection";
import { LineItem } from "../LineItem";
import { StudentSection, studentDataIsValid } from "../StudentSection";
import { SubmitButton } from "../SubmitButton";
import { Upsell } from "../Upsell";
import { PaymentTotals } from "./PaymentTotals";

import type { TGalaxyError } from "@up/data";

function getLineItemErrors(errors: Array<TGalaxyError>, id: string) {
  return errors.filter((error) => {
    return error.path[0] === "lineItems" && error.path[1] === id;
  });
}

type ReviewStepProps = {
  isFree: boolean;
  showDOB: boolean;
  showGuardianEmail: boolean;
};

export function ReviewStep({
  isFree,
  showDOB,
  showGuardianEmail,
}: ReviewStepProps) {
  const theme = useTheme();
  const cart = useCart();
  // check is to make typescript happy
  // guard is in wrapper component
  if (!cart.data) return null;

  const items = cart.data.lineItems?.length ? cart.data.lineItems : [];

  return (
    <>
      <StudentSection cart={cart.data} dobRequired={showDOB} />

      {!theme.noGuardian ? (
        <GuardianSection
          showGuardianEmail={showGuardianEmail}
          cart={cart.data}
        />
      ) : null}

      <section
        className="checkout-section mb-12 scroll-mt-16"
        id="review-section"
      >
        <h2 className={`checkout-heading mb-2 ${theme.heading2Class}`}>
          Courses
        </h2>

        {cart.data.cartDiscount?.heading && !cart.data.coupon ? (
          <div className="mb-2">
            <Toast size="large" variant="feature">
              <ToastTitle variant="feature">
                {cart.data.cartDiscount.heading}
              </ToastTitle>
              {cart.data.cartDiscount.message}
            </Toast>
          </div>
        ) : null}

        <ul className="checkout-list flex flex-col gap-2 m-0 list-none p-0">
          {items.map((item) => (
            <LineItem
              canChangeDate={true}
              key={item._id}
              errors={
                cart.data?.errors
                  ? getLineItemErrors(cart.data.errors, item._id)
                  : undefined
              }
              item={item}
              removeFn={cart.removeItem}
            />
          ))}
        </ul>

        <div aria-hidden="true" className="h-4" />

        {cart.data.summary.message && cart.data.summary.heading ? (
          <div className="rounded p-4 border border-gray-200 bg-white">
            <span className="checkout-token">{cart.data.summary.heading}</span>
            <div
              className="mt-2 leading-tight opacity-70"
              style={{ fontSize: "15px" }}
            >
              {cart.data.summary.message}
            </div>
          </div>
        ) : null}

        <div aria-hidden="true" className="h-2" />

        <Upsell
          analytics={{
            location: "checkout",
            lineItemCount: String(cart.data.lineItems?.length ?? 0),
          }}
          openDefault={false}
          showToggle={Boolean(items?.length)}
          title={
            items?.length
              ? "Interested in another course?"
              : "Interested in adding a course?"
          }
        />
      </section>

      <section
        className="checkout-section mb-12 scroll-mt-16"
        id="discount-code-section"
      >
        <h2 className={`checkout-heading mb-2 ${theme.heading2Class}`}>
          Discount Code
        </h2>
        <AvailableCodes />
        <div className="bg-gray-100 p-4 rounded">
          <DiscountSection />
        </div>
      </section>

      <AnimatePresence>
        {cart.data?.suggested?.length ? (
          <motion.section
            className="checkout-section mb-12 overflow-hidden"
            {...MOTION["expand"].props}
            id="upgrade-section"
          >
            <h2 className={`checkout-heading mb-2 ${theme.heading2Class}`}>
              Enhance Your Learning Experience
            </h2>

            <div className="mb-2">
              <Toast size="large" variant="feature">
                Add <span className="italic">+Live</span> for engaging live
                online sessions featuring experts in the field, course mentors,
                and classmates from around the world. We're currently only
                offering Medicine and Law & Government{" "}
                <span className="italic">+Live</span> options but all students
                are welcome to participate.
              </Toast>
            </div>

            <ul className="checkout-list m-0 list-none p-0">
              {cart.data.suggested?.map((addon) => {
                return (
                  <AddonLineItem
                    key={addon._id}
                    addon={addon}
                    addItem={cart.addItem}
                  />
                );
              })}
            </ul>
          </motion.section>
        ) : null}
      </AnimatePresence>

      {cart.addItemError ? (
        <div className="mb-4">
          <Toast variant="error">{cart.addItemError}</Toast>
        </div>
      ) : null}

      {cart.hasBlockingErrors || !studentDataIsValid(cart.data, showDOB) ? (
        <BlockingErrorsMessage errors={cart.data?.errors} />
      ) : null}

      {cart.checkoutErrorMessage ? (
        <div className="mt-0 mb-4 pt-4">
          <Toast variant="error">{cart.checkoutErrorMessage}</Toast>
        </div>
      ) : cart.submitAsCompleteDidError ? (
        <div className="mt-0 mb-4 pt-4">
          <Toast variant="error">
            We could not complete your checkout. Please try again or contact
            support at{" "}
            <a className="underline" href={`mailto:${theme.supportEmail}`}>
              {theme.supportEmail}
            </a>
          </Toast>
        </div>
      ) : null}

      {isFree ? <FinalAgreement /> : null}

      <div className="flex flex-col md:items-center justify-between pt-4 border-t border-dashed border-gray-300 md:pt-0 md:border-none md:flex-row">
        <div className="pb-2 md:pb-0 md:order-2 self-end">
          <PaymentTotals />
        </div>
        <div className="flex items-center gap-3 flex-grow">
          <SubmitButton
            disabled={
              cart.isValidating ||
              cart.isSubmitting ||
              cart.hasBlockingErrors ||
              !studentDataIsValid(cart.data, showDOB)
            }
            isSubmitting={cart.isSubmitting || cart.isValidating}
            onClick={async () => {
              if (!studentDataIsValid(cart.data, showDOB)) {
                document
                  .getElementById("student-section")
                  ?.scrollIntoView({ behavior: "smooth" });
                return;
              }

              const { error } = await cart.finalValidate();
              if (error) {
                return;
              }

              if (isFree) {
                cart.handleSubmitAsFree();
              } else {
                cart.navigateToStep(1);
              }
            }}
          >
            {isFree ? "Complete Your Enrollment" : "Continue to Payment"}
          </SubmitButton>
        </div>
      </div>
      <br />
    </>
  );
}
